import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Layers,
  ShieldCheck,
  Sun,
  Sparkles,
  Hourglass,
  Palette,
  Home as HomeIcon,
  ArrowRight,
  Check,
  Instagram,
  Trophy,
} from "lucide-react";
import { IMG } from "@/lib/images";
import ContactForm from "@/components/ContactForm";
import BeforeAfter from "@/components/BeforeAfter";

const benefits = [
  { icon: Layers, title: "Fugenlos & modern", text: "Nahtlose Oberfläche, klare Linien." },
  { icon: ShieldCheck, title: "Rutschfest & sicher", text: "Trittsichere Struktur, auch bei Nässe." },
  { icon: Sun, title: "Wetter- & UV-beständig", text: "Langlebig im Außenbereich." },
  { icon: Sparkles, title: "Pflegeleicht", text: "Einfache Reinigung, dauerhafte Optik." },
  { icon: Hourglass, title: "Sehr langlebig", text: "Robust gegen Belastung und Zeit." },
  { icon: Palette, title: "Individuell gestaltbar", text: "Vielfältige Farben & Kombinationen." },
  { icon: HomeIcon, title: "Innen & Außen", text: "Universell einsetzbar – ein System." },
];

const areas = [
  { name: "Terrasse", img: IMG.areas.terrasse, span: "md:col-span-2 md:row-span-2" },
  { name: "Balkon", img: IMG.areas.balkon, span: "" },
  { name: "Treppe", img: IMG.areas.treppe, span: "" },
  { name: "Eingangsbereich", img: IMG.areas.eingang, span: "md:col-span-2" },
  { name: "Gehwege", img: IMG.areas.gehweg, span: "" },
  { name: "Innenräume", img: IMG.areas.innen, span: "" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 0.61, 0.36, 1] },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ================ HERO ================ */}
      <section
        data-testid="hero-section"
        className="relative min-h-[88vh] flex items-end overflow-hidden grain"
      >
        <img
          src={IMG.hero}
          alt="Moderne Terrasse mit Steinteppich"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-20 pt-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
            className="max-w-3xl text-white"
          >
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-white/70 mb-6">
              <span className="w-8 h-px bg-[#E10600]" />
              Linea Bodendesign
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-light tracking-tight">
              Moderne Steinteppiche für{" "}
              <span className="italic font-medium text-[#E10600]">Innen-</span> und{" "}
              <span className="italic font-medium text-[#E10600]">Außenbereiche</span>.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
              Fugenlose, langlebige und rutschfeste Bodenlösungen für Balkone,
              Terrassen, Treppen und Eingangsbereiche.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#1C1C1C] text-sm tracking-wide hover:bg-[#E10600] hover:text-white transition-colors duration-300"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] group-hover:bg-white transition-colors" />
                Kostenlose Beratung anfragen
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/projekte"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/40 text-white text-sm tracking-wide hover:border-[#E10600] hover:text-[#E10600] transition-colors"
              >
                Projekte ansehen
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================ INTRO ================ */}
      <section data-testid="intro-section" className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="space-y-8">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500">
              Über uns
            </div>
            <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-[1.25] text-[#1C1C1C] font-light">
              Linea Bodendesign plant und realisiert hochwertige
              Steinteppich-Bodenlösungen für private und gewerbliche
              Projekte. Unser Fokus: langlebige Qualität, moderne Optik
              und individuelle Umsetzung – mit präzisem Handwerk.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ================ BENEFITS ================ */}
      <section
        data-testid="benefits-section"
        className="py-24 sm:py-32 bg-white border-y border-stone-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              Vorteile
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Warum Steinteppich.
            </h2>
          </motion.div>

          <div
            data-testid="benefits-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12"
          >
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group"
                data-testid={`benefit-item-${i}`}
              >
                <b.icon
                  size={28}
                  strokeWidth={1.4}
                  className="text-[#1C1C1C] mb-5"
                />
                <h3 className="font-heading text-lg font-medium mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {b.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ APPLICATION AREAS ================ */}
      <section data-testid="areas-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-14 gap-6">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
                Einsatzbereiche
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
                Ein System.<br />Sechs Bereiche.
              </h2>
            </div>
            <p className="text-stone-600 max-w-md text-sm leading-relaxed">
              Steinteppich passt sich an – ob hochfrequentierter Eingang,
              sonniger Balkon oder Wohnraum. Wir planen jede Fläche individuell.
            </p>
          </motion.div>

          <div
            data-testid="areas-grid"
            className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[220px] md:auto-rows-[260px]"
          >
            {areas.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className={`relative overflow-hidden group ${a.span}`}
                data-testid={`area-tile-${a.name.toLowerCase()}`}
              >
                <img
                  src={a.img}
                  alt={a.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-white/70">
                    Bereich
                  </div>
                  <div className="font-heading text-2xl text-white mt-1">
                    {a.name}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ WHY US ================ */}
      <section data-testid="why-us-section" className="py-24 sm:py-32 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp} className="relative aspect-[4/5] overflow-hidden">
            <img
              src={IMG.workshop}
              alt="Präzises Handwerk"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
          <motion.div {...fadeUp} className="space-y-8">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500">
              Warum Linea
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Premium-Handwerk.<br />Sichtbar im Detail.
            </h2>
            <ul className="space-y-5 pt-2" data-testid="why-us-list">
              {[
                "Persönliche Beratung – vor Ort und auf Augenhöhe",
                "Hochwertige Materialien von etablierten Herstellern",
                "Saubere, präzise Ausführung jedes Quadratmeters",
                "Maßgeschneiderte Lösungen für jeden Bereich",
                "Zuverlässige Termine und transparente Kommunikation",
              ].map((t, i) => (
                <li key={i} className="flex gap-4 items-start" data-testid={`why-item-${i}`}>
                  <Check size={20} strokeWidth={1.8} className="mt-0.5 shrink-0 text-[#1C1C1C]" />
                  <span className="text-[15px] text-stone-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ================ MATERIALS ================ */}
      <section data-testid="materials-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              <span className="w-8 h-px bg-[#E10600]" />
              Farb- & Designvielfalt
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              10 Farben.<br />Unzählige <span className="text-[#E10600] italic font-medium">Kombinationen</span>.
            </h2>
            <p className="text-stone-600 mt-6 text-base leading-relaxed max-w-xl">
              Vom strahlenden Carrara-Weiß bis zum tiefen Nero Ebano –
              wählen Sie aus zehn echten Natursteinen. Alle Farben sind
              frei kombinierbar und ergeben Ihre individuelle Bodensignatur.
            </p>
          </motion.div>

          <div
            data-testid="materials-grid"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4"
          >
            {IMG.textures.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
                className="group"
                data-testid={`material-tile-${i}`}
              >
                <div className="aspect-square overflow-hidden bg-stone-100 relative">
                  <img
                    src={t.url}
                    alt={t.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
                </div>
                <div className="mt-3">
                  <div className="text-sm font-medium tracking-wide">{t.name}</div>
                  <div className="text-[10px] text-stone-500 uppercase tracking-[0.2em] mt-1">
                    Steinteppich
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-sm text-stone-500 italic max-w-2xl">
            Alle 10 Farben können beliebig miteinander kombiniert werden –
            so entstehen einzigartige, auf Sie zugeschnittene Bodenoptiken.
          </p>
        </div>
      </section>

      {/* ================ GALLERY / REFERENCES ================ */}
      <section data-testid="gallery-section" className="py-24 sm:py-32 bg-[#D4C4B7]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              Referenzen
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Ausgewählte Projekte.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-grid">
            {IMG.gallery.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`overflow-hidden ${i % 5 === 0 ? "sm:row-span-2 aspect-[3/4]" : "aspect-[4/3]"}`}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={src}
                  alt={`Projekt ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/projekte"
              data-testid="gallery-cta"
              className="inline-flex items-center gap-2 text-sm font-medium border-b border-[#1C1C1C] pb-1 hover:gap-3 transition-all"
            >
              Alle Projekte ansehen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ================ BEFORE / AFTER ================ */}
      <section data-testid="beforeafter-section" className="py-24 sm:py-32 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
              <span className="w-8 h-px bg-[#E10600]" />
              Vorher / Nachher
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              Der Unterschied,<br />den Steinteppich macht.
            </h2>
            <p className="text-stone-600 mt-6 text-base leading-relaxed max-w-xl">
              Bewegen Sie den Regler – und sehen Sie selbst, wie aus einer
              alten Treppe ein moderner, fugenloser Eingangsbereich wird.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <div className="aspect-[16/10] sm:aspect-[16/9] max-h-[720px] mx-auto">
              <BeforeAfter
                beforeSrc={IMG.beforeAfter[0].before}
                afterSrc={IMG.beforeAfter[0].after}
                className="w-full h-full"
              />
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading text-lg font-medium">{IMG.beforeAfter[0].title}</div>
                <div className="text-xs text-stone-500 uppercase tracking-[0.2em] mt-1">
                  Steinteppich · {IMG.beforeAfter[0].color}
                </div>
              </div>
              <div className="text-xs text-stone-500 italic">
                Tipp: Regler ziehen oder Pfeiltasten verwenden →
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================ SPONSORING ================ */}
      <section data-testid="sponsoring-section" className="py-24 sm:py-32 bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute -top-px left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#E10600] to-transparent opacity-60" />

        {/* subtle red glow accent */}
        <div className="pointer-events-none absolute -left-40 top-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full bg-[#E10600]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section header */}
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/60 mb-4">
              <span className="w-8 h-px bg-[#E10600]" />
              Sponsoring
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">
              Stolzer Sponsor von{" "}
              <span className="text-[#E10600] italic font-medium">LineaMotion Racing</span>.
            </h2>
            <p className="mt-6 text-white/75 leading-relaxed max-w-2xl">
              Wir unterstützen den jungen Kartfahrer{" "}
              <span className="text-white font-medium">Joshua Ernst</span>.
              Präzision, Leidenschaft und Geschwindigkeit – Werte, die uns auf
              der Strecke und im Handwerk verbinden.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-10 items-stretch">
            {/* LEFT: Driver Card with logo + number */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-4 bg-[#141414] border border-white/10 p-8 flex flex-col"
              data-testid="driver-card"
            >
              <img
                src={IMG.racing.logo}
                alt="LineaMotion Racing Team"
                className="h-24 w-auto object-contain self-start mb-8"
                style={{ filter: "url(#brand-light)" }}
              />

              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Fahrer</div>
              <div className="font-heading text-2xl text-white mb-1">Joshua Ernst</div>
              <a
                href="https://www.instagram.com/joshuaernst_/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sponsoring-instagram-link"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#E10600] transition-colors w-fit"
              >
                <Instagram size={14} strokeWidth={1.6} />
                @joshuaernst_
              </a>

              <div className="mt-auto pt-8 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Startnummer</div>
                <div className="font-heading text-7xl font-light text-[#E10600] leading-none">29</div>
              </div>
            </motion.div>

            {/* RIGHT: Current + Goal cards stacked + Photos */}
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {/* Current season */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="bg-[#141414] border border-white/10 p-8 flex flex-col justify-between min-h-[230px]"
                data-testid="current-season-card"
              >
                <div>
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/60 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E10600] animate-pulse" />
                    Aktuell · Saison 2026
                  </div>
                  <div className="font-heading text-3xl sm:text-4xl text-white leading-tight">
                    KartMasters<br />
                    <span className="text-white/60 text-2xl sm:text-3xl">Nord</span>
                  </div>
                </div>
                <div className="text-sm text-white/60 mt-6">
                  Nationale Kart-Rennserie in Norddeutschland
                </div>
              </motion.div>

              {/* Goal 2026 */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="bg-[#E10600] text-white p-8 flex flex-col justify-between min-h-[230px] relative overflow-hidden"
                data-testid="goal-2026-card"
              >
                <div className="absolute -right-10 -bottom-10 w-44 h-44 rounded-full border-[12px] border-white/15" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/80 mb-4">
                    <Trophy size={12} strokeWidth={2} />
                    Ziel · Saison 2027
                  </div>
                  <div className="font-heading text-3xl sm:text-4xl leading-tight">
                    Deutsche &<br />
                    <span className="italic font-medium">Weltmeisterschaft</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="px-2.5 py-1 rounded-full bg-white/20 text-[11px] tracking-wider font-medium">
                      DM · GIKC
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-white/20 text-[11px] tracking-wider font-medium">
                      WM · KWC
                    </span>
                  </div>
                </div>
                <div className="relative text-sm text-white/85 mt-6">
                  Teilnahme an GIKC (DM) und KWC (WM) in der Kart-Klasse
                </div>
              </motion.div>

              {/* Photos row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="aspect-[3/4] sm:aspect-auto sm:h-[280px] overflow-hidden"
                data-testid="sponsoring-image-portrait"
              >
                <img
                  src={IMG.racing.portrait}
                  alt="Joshua Ernst – Portrait"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="aspect-[3/4] sm:aspect-auto sm:h-[280px] overflow-hidden"
                data-testid="sponsoring-image-action"
              >
                <img
                  src={IMG.racing.action}
                  alt="Joshua Ernst – Auf der Strecke"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ================ CTA / CONTACT ================ */}
      <section data-testid="cta-section" className="py-24 sm:py-32 bg-[#1C1C1C] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/60 mb-6">
              <span className="w-8 h-px bg-[#E10600]" />
              Beratung
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              Ihr neuer Boden<br />beginnt <span className="text-[#E10600] italic font-medium">hier</span>.
            </h2>
            <p className="mt-8 text-white/75 text-base leading-relaxed max-w-md">
              Vereinbaren Sie eine unverbindliche Beratung. Wir besichtigen
              Ihre Fläche, beraten Sie persönlich und erstellen ein
              transparentes Angebot.
            </p>

            <div className="mt-12 space-y-4 text-sm text-white/80">
              <div data-testid="cta-phone">Telefon: <a href="tel:+491711584130" className="hover:text-white">+49 171 158 41 30</a></div>
              <div data-testid="cta-email">E-Mail: <a href="mailto:info@linea-boden-design.de" className="hover:text-white">info@linea-boden-design.de</a></div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="bg-white text-[#1C1C1C] p-8 sm:p-10 rounded-sm">
            <ContactForm compact />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
